import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import Wire from './latest-wires-components/wire';
import {
  SectionContainer,
  SectionTitle,
  SectionSubTitle,
  SectionDetails,
} from '../../shared/sectionable/styles';
import Tail from '../common/styles';
import {
  useGetPaginatedAllWiresOfContributor,
  useGetPaginatedAllWiresOfFund,
} from '../../../services/wires/from-contributor-service';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import { scrollIfHashMatch } from '../../shared/navigation/utils';
import useContributorStateStore from '../common/contributor-state-hook';

const INIT_WIRE_NUMBER = 3;
const MAX_WIRE_NUMBER = 10;

const Container = styled(SectionContainer)`
  ${props => props.theme.isXXLarge && `
    margin-bottom: 94px;
  `};
`;

const sectionName = 'latest-wires';
export default function LatestWires({ profile, viewAllWiresLink, type }) {
  const { id, slug } = profile;
  const [{ data }, getRecentWires] = type === 'profile'
    ? useGetPaginatedAllWiresOfContributor(id)
    : useGetPaginatedAllWiresOfFund(id);
  const [showViewMore, setShowViewMore] = React.useState(false);
  const [showViewAll, setShowViewAll] = React.useState(false);
  const [wires, setWires] = React.useState([]);
  const set = useContributorStateStore(state => state.set);

  const buildEventData = (event, statisticable) => ({
    event,
    statisticable,
    id,
    page: window.location.pathname,
  });

  const onViewMore = e => {
    e.preventDefault();
    const eventName = type === 'profile'
      ? 'contributor_profile_latest_view_more_clicked'
      : 'fund_profile_latest_view_more_clicked';
    recordStatisticsEvent(buildEventData(eventName, type));
    setWires(_.slice(data.wires, 0, MAX_WIRE_NUMBER));
    setShowViewMore(false);
    setShowViewAll(data.total > MAX_WIRE_NUMBER);
  };

  const onViewAllWires = e => {
    e.preventDefault();
    const eventName = type === 'profile'
      ? 'contributor_profile_latest_view_all_clicked'
      : 'fund_profile_latest_view_all_clicked';
    recordStatisticsEvent(buildEventData(eventName, type));
    if (viewAllWiresLink && viewAllWiresLink.length > 0) {
      window.location = `${viewAllWiresLink}`;
    } else {
      window.location = `/search/contributor/${slug}?nextPage=true&tab=all`;
    }
  };

  useChangeAwareEffect(({ isMount }) => {
    isMount && scrollIfHashMatch(sectionName);
  }, {});

  React.useEffect(() => {
    getRecentWires(1);
  }, [getRecentWires]);

  React.useEffect(() => {
    const wiresLength = _.get(data, 'wires.length');
    if (wiresLength > 0) {
      setWires(_.slice(data.wires, 0, INIT_WIRE_NUMBER));
      setShowViewMore(wiresLength > INIT_WIRE_NUMBER);
      set(state => {
        state.hasLatestWires = true;
      });
    }
  }, [data, set]);

  if (!_.get(data, 'wires.length')) {
    return null;
  }

  const comparator = moment().subtract(30, 'days');
  const last30Days = _.filter(data.wires, w => comparator.isBefore(w.firstPublishedAt, 'day')).length;

  return (
    <Container name={sectionName}>
      <SectionTitle>
        Latest Wires
        {last30Days > 0 && (
          <SectionSubTitle>
            {`${last30Days} ${last30Days > 1 ? 'wires' : 'wire'} posted in the last 30 days`}
          </SectionSubTitle>
        )}
      </SectionTitle>
      <SectionDetails>
        {!!wires.length && (
          <article>
            {wires.map(wire => <Wire key={`latest-wire-${wire.id}`} wire={wire} profile={wire.profile} />)}
            {showViewMore && (
              <Tail>
                <button type="button" onClick={onViewMore} className="view-more">view more </button>
              </Tail>
            )}
            {showViewAll && (
              <Tail>
                <button type="button" onClick={onViewAllWires} className="view-all">view all wires</button>
              </Tail>
            )}
          </article>
        )}
      </SectionDetails>
    </Container>
  );
}

LatestWires.propTypes = {
  type: PropTypes.number.isRequired,
  viewAllWiresLink: PropTypes.string.isRequired,
  profile: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
};
